import React, {
  ComponentPropsWithoutRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import PropTypes from 'prop-types';

import { AnimatePresence } from '@adsk/alloy-react-animation';
import { useForwardedRef, variantSwitch } from '@adsk/alloy-react-helpers';
import Portal from '@adsk/alloy-react-portal';
import { useTheme } from '@adsk/alloy-react-styles';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import {
  Overlay,
  Header,
  Body,
  Footer,
  Close,
  PrevArrow,
  NextArrow,
} from './layout';
import ModalContext from './ModalContext';
import useFocusTrap from './useFocusTrap';

/**
 * This is here instead of inline to avoid having to disable jsx-a11y lint rules which report onClick.
 * In this case, we want to stop propagation of the event to prevent child overlays from closing the modal
 */
const eventPreventionProps = {
  onClick: (e: React.MouseEvent<HTMLDialogElement>) => e.stopPropagation(),
};

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  EXTRA_LARGE: 'extra_large',
} as const;

type Sizes = typeof SIZES;
export type Size = Sizes[keyof Sizes] | undefined;

type OverlayProps = ComponentPropsWithoutRef<typeof Overlay>;
type ModalProps = {
  open?: boolean;
  width?: string | number;
  height?: string | number;
  container?: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  hideOverlay?: boolean;
  onClose?: (e?: React.MouseEvent) => void;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  size?: Size;
} & Pick<
  OverlayProps,
  'onAnimationStart' | 'onAnimationComplete' | 'initial' | 'animate' | 'exit'
> & {
    overlayStyle?: OverlayProps['style'];
  };

/**
 * A modal is an overlay that focuses the customer's attention on a single task or set of controls.
 * It creates a mode that disables the main window but keeps it visible with the modal window as a child window in front of it.
 * Users must interact with the modal window before they can return to the parent application.
 */
const Modal = React.forwardRef<
  HTMLDialogElement,
  ModalProps & Omit<StylableComponent<HTMLDialogElement>, 'onAnimationStart'>
>(
  (
    {
      children,
      className,
      style,
      open,
      height,
      width,
      container = document.body,
      hideOverlay,
      onClose,
      onAnimationStart,
      onAnimationComplete,
      overlayStyle,
      shouldCloseOnEsc = true,
      shouldCloseOnOverlayClick = true,
      size = SIZES.MEDIUM,
      initial,
      animate,
      exit,
      ...props
    },
    ref,
  ) => {
    const dynamicTheme = useTheme();
    const modalTokens = dynamicTheme.tokens?.component?.modal;
    const modalRef = useForwardedRef(ref);

    const focusContainer = useFocusTrap(modalRef, open === true);
    const mouseDownTargetRef = useRef<EventTarget | null>(null);

    const handleAnimationComplete = useCallback(
      (
        definition: Parameters<
          NonNullable<OverlayProps['onAnimationComplete']>
        >[0],
      ) => {
        focusContainer();
        onAnimationComplete?.(definition);
      },
      [focusContainer, onAnimationComplete],
    );

    const onOverlayMouseDown: React.MouseEventHandler<HTMLElement> =
      useCallback((e) => {
        mouseDownTargetRef.current = e.target;
      }, []);

    const onClick: React.MouseEventHandler<HTMLElement> = useCallback(
      (e) => {
        if (!modalRef.current) return;
        if (modalRef.current.contains(e.target as Node)) {
          return;
        }
        if (modalRef.current.contains(mouseDownTargetRef.current as Node)) {
          return;
        }
        shouldCloseOnOverlayClick && onClose?.(e);
      },
      [modalRef, shouldCloseOnOverlayClick, onClose],
    );

    const onEscape = useCallback(
      (e: KeyboardEvent) => {
        onClose?.();
        e.stopPropagation();
      },
      [onClose],
    );

    const context = useMemo(() => ({ shouldCloseOnEsc }), [shouldCloseOnEsc]);

    return (
      <Portal container={container}>
        <AnimatePresence>
          {open && (
            <Overlay
              data-testid="modal-overlay"
              hide={hideOverlay}
              onMouseDown={onOverlayMouseDown}
              onClick={onClick}
              onAnimationStart={onAnimationStart}
              onAnimationComplete={handleAnimationComplete}
              initial={initial}
              animate={animate}
              exit={exit}
              style={overlayStyle}
              onEscape={shouldCloseOnEsc ? onEscape : undefined}
            >
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- <dialog> has implicit role="dialog" and is interactive; onMouseDown tracks drag origin to prevent accidental overlay-close on drag-out */}
              <dialog
                open
                tabIndex={-1}
                aria-modal
                {...props}
                className={className}
                {...eventPreventionProps}
                onMouseDown={onOverlayMouseDown}
                ref={modalRef}
                css={[
                  {
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0,
                    margin: 0,
                    border: 'none',
                    color: 'inherit',
                    overflow: 'visible',
                    maxWidth: 'calc(100vw - 32px)',
                    width:
                      width ||
                      variantSwitch({
                        statement: size,
                        variants: {
                          [SIZES.SMALL]: 420,
                          [SIZES.MEDIUM]: 600,
                          [SIZES.LARGE]: 800,
                          [SIZES.EXTRA_LARGE]: 1140,
                        },
                      }) ||
                      undefined,
                    maxHeight: 'calc(100vh - 120px)',
                    minHeight: 138,
                    height:
                      height ||
                      variantSwitch({
                        statement: size,
                        variants: {
                          [SIZES.SMALL]: 359,
                          [SIZES.MEDIUM]: 480,
                          [SIZES.LARGE]: 644,
                          [SIZES.EXTRA_LARGE]: 733,
                        },
                      }) ||
                      undefined,
                    outline: 'none',
                    fontFamily: modalTokens?.['font-family'],
                    backgroundColor: modalTokens?.['background-color'],
                    borderRadius: modalTokens?.['border-radius'],
                    boxShadow: modalTokens?.['box-shadow'],
                    zIndex: theme.zIndex.modal,
                    ...(hideOverlay && {
                      pointerEvents: 'all',
                    }),
                  },
                  style,
                ]}
              >
                <ModalContext.Provider value={context}>
                  {children}
                </ModalContext.Provider>
              </dialog>
            </Overlay>
          )}
        </AnimatePresence>
      </Portal>
    );
  },
);

Modal.displayName = 'Modal';

Modal.propTypes = {
  /** The content of modal */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** is modal open */
  open: PropTypes.bool,
  /** modal width, default is 400px */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** modal height, default is 400px */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Container Used for attaching the modal as a child component to the container */
  container: PropTypes.object,
  /** hide modal Overlay */
  hideOverlay: PropTypes.bool,
  /** called on click close modal */
  onClose: PropTypes.func,
  /** A callback that's called when the modal has started open animation */
  onAnimationStart: PropTypes.func,
  /** A callback that's called when the modal has Complete Animation close animation */
  onAnimationComplete: PropTypes.func,
  /** Set whether modal should be closed when pressing on esc button */
  shouldCloseOnEsc: PropTypes.bool,
  /** Set whether modal should be closed when click on overlay */
  shouldCloseOnOverlayClick: PropTypes.bool,
  /** Modal size */
  size: PropTypes.oneOf(Object.values(SIZES)),
};

export default Object.assign(Modal, {
  Overlay,
  Header,
  Body,
  Footer,
  Close,
  PrevArrow,
  NextArrow,
  SIZES,
});
